import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  inject,
  input,
  viewChild,
  viewChildren,
  effect,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Menu } from 'primeng/menu';
import { Subject, of, take, takeUntil, catchError, map } from 'rxjs';
import { MessageService, ConfirmationService, MenuItem, PrimeTemplate } from 'primeng/api';
import { Popover, PopoverModule } from 'primeng/popover';
import { NotesPanelV2Service } from './services/notes-panel-v2.service';
import { Note, NotesFilter, NoteUserOption, FilterTag, DEFAULT_NOTES_FILTER, LOAN_NOTE_TAG_ORDER } from './models/note.model';
import { CurrentUserService } from '../../services/current-user.service';
import { NoteListStore } from '../../state/note-list.store';
import { NoteListQuery } from '../../state/note-list.query';
import { ButtonDirective } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Editor } from 'primeng/editor';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AsyncPipe, DatePipe, DecimalPipe, NgStyle } from '@angular/common';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagsChipsComponent } from '../tags-chips/tags-chips.component';
import { SortNoteTagsPipe } from './pipes/sort-note-tags.pipe';
import { Models } from '../../../pipeline/models/global.models';
import { TagsRepository } from '../../../pipeline/stores/api/tags.repository';
import { sortByTagOrder, stripHtml } from '../../utils';

/** Maximum character limit for new V2 notes (stored in LoanNote table, VARCHAR(8000)) */
const MAX_NOTE_LENGTH_V2 = 8000;

/**
 * Maximum character limit for legacy V1 notes (stored in LoanColorMapping table).
 * V1 uses 4 columns (Notes, Notes2, Notes3, Notes4) of 8000 chars each = 32000 total.
 * Using 25000 as practical limit to allow buffer.
 */
const MAX_NOTE_LENGTH_LEGACY = 25000;

/** Menu item IDs for note actions menu */
const MENU_ITEM_ID = {
  EDIT: 'edit-note',
  DELETE: 'delete-note',
} as const;

/**
 * Height threshold (in pixels) for collapsing notes per BRD FR-2.3.
 * Notes exceeding this height will show "View More" link.
 */
const COLLAPSE_HEIGHT_PX = 300;

/**
 * Minimum time difference (ms) between create and update to show "edited" indicator.
 * On create, both timestamps are set in the same transaction (differ by milliseconds).
 * 1 second provides ample buffer while catching legitimate quick edits.
 */
const EDITED_THRESHOLD_MS = 1000;

/**
 * Notes Panel V2 - Content component for viewing and managing loan notes.
 *
 * SCOPE: Loans only. Leads, applications, and partners continue to use V1.
 *
 * Note: This is a content-only component. The parent provides the
 * sidebar/drawer container (Pipeline, URLA2, or Dashboard).
 *
 * Integration pattern (in parent templates):
 * ```html
 * @if (isNotesV2Enabled && pipelineType === PipelineType.Loan) {
 *   <cmg-notes-panel-v2 [fileDataId]="loan.fileDataId" />
 * } @else {
 *   <cmg-clear2-ui-notes-panel [pipelineType]="pipelineType" ... />
 * }
 * ```
 */
@Component({
  selector: 'cmg-notes-panel-v2',
  templateUrl: './notes-panel-v2.component.html',
  styleUrl: './notes-panel-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    Tooltip,
    Editor,
    ReactiveFormsModule,
    FormsModule,
    PrimeTemplate,
    ProgressSpinner,
    Menu,
    ConfirmDialog,
    AsyncPipe,
    DatePipe,
    DecimalPipe,
    NgStyle,
    PopoverModule,
    DatePickerModule,
    CheckboxModule,
    MultiSelectModule,
    TagsChipsComponent,
    SortNoteTagsPipe,
  ],
})
export class NotesPanelV2Component implements OnInit, OnChanges, OnDestroy {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _confirmationService = inject(ConfirmationService);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _elementRef = inject(ElementRef);
  private readonly _messageService = inject(MessageService);
  private readonly _noteListQuery = inject(NoteListQuery);
  private readonly _noteListStore = inject(NoteListStore);
  private readonly _notesPanelService = inject(NotesPanelV2Service);
  private readonly _tagsRepository = inject(TagsRepository);

  /** The loan's fileDataId - primary identifier for loading notes */
  readonly fileDataId = input<number>(undefined);

  /** Incrementing counter to force a refresh even when fileDataId hasn't changed */
  readonly refreshTrigger = input(0);

  /** Reference to the note actions popup menu */
  readonly noteMenu = viewChild<Menu>('noteMenu');

  /** Reference to the filter overlay panel */
  readonly filterPanel = viewChild<Popover>('filterPanel');

  /** References to note content elements for height measurement */
  readonly noteContentElements = viewChildren<ElementRef<HTMLDivElement>>('noteContent');

  /** PipelineType enum exposed for template */
  readonly PipelineType = Models.PipelineType;

  /** Tag display order exposed for template (passed to tags-chips) */
  readonly loanNoteTagOrder = LOAN_NOTE_TAG_ORDER;

  // ============================================================
  // UI State (local signals - transient, not tracked for dirty checking)
  // ============================================================

  /** Loading state for initial load */
  isLoading = signal(false);

  /** Saving state for create/update/delete operations */
  isSaving = signal(false);

  /** Error message if API call fails */
  errorMessage = signal<string | null>(null);

  /** Whether the editor is open (for creating or editing) */
  isEditorOpen = signal(false);

  /** Track which notes are expanded (for "View More/Less") */
  expandedNotes = signal<Set<string>>(new Set());

  /** Current user's GUID for determining edit/delete permissions */
  currentUserGuid = signal<string | null>(null);

  /** Whether the search bar is visible */
  isSearchOpen = signal(false);

  /** Current search input value */
  searchTerm = signal('');

  /** Active search query (set when user clicks Search button) */
  activeSearchQuery = signal('');

  /** Whether the filter panel is open */
  isFilterOpen = signal(false);

  /** Pending filter values (being edited in filter panel, not yet applied) */
  pendingFilters = signal<NotesFilter>({ ...DEFAULT_NOTES_FILTER });

  /** Active filter values (applied and affecting displayed notes) */
  activeFilters = signal<NotesFilter>({ ...DEFAULT_NOTES_FILTER });

  /** Tag GUIDs for the note being edited (populated on edit, cleared on close) */
  editingTagGuids = signal<string[]>([]);

  // ============================================================
  // Store-backed State (read from NoteListQuery - no local copies)
  // ============================================================

  /** Notes list - read from store */
  readonly notes$ = this._noteListQuery.notes$;

  /** Editor content - read from store */
  readonly editorContent$ = this._noteListQuery.editorContent$;

  /** The GUID of the note being edited - read from store */
  readonly editingNoteGuid$ = this._noteListQuery.editingNoteGuid$;

  /** Available LoanNote category tags for filter dropdown, sorted by LOAN_NOTE_TAG_ORDER */
  readonly availableCategories$ = this._tagsRepository.all$.pipe(
    map((tags) => {
      const filtered = tags.filter((t) => t.pipelineType === Models.PipelineType.LoanNote && t.isGlobal);
      return sortByTagOrder(filtered, LOAN_NOTE_TAG_ORDER);
    }),
  );

  /** Signal version for synchronous access in methods like getActiveFilterTags */
  readonly availableCategories = toSignal(this.availableCategories$, { initialValue: [] as (Models.Tag & { guid: string })[] });

  /** Whether currently editing a legacy note (sync access via toSignal) */
  isEditingLegacyNote = toSignal(
    this._noteListQuery.select((s) => s.isEditingLegacyNote),
    { initialValue: false },
  );

  /**
   * Maximum note length - varies by note type.
   * V2 notes: 8000 chars (stored in LoanNote table)
   * Legacy notes: 25000 chars (stored in LoanColorMapping table)
   */
  get maxNoteLength(): number {
    return this.isEditingLegacyNote() ? MAX_NOTE_LENGTH_LEGACY : MAX_NOTE_LENGTH_V2;
  }

  /** Track which notes exceed the height threshold and need collapsing */
  overflowingNotes = signal<Set<string>>(new Set());

  /** Unique key for this component's confirmation dialog */
  readonly confirmDialogKey = 'notesPanelV2';

  /** Menu items for note card ellipsis menu */
  noteMenuItems: MenuItem[] = [];

  /** Currently active note for menu actions */
  private _activeNote: Note | null = null;

  private readonly _destroyed$ = new Subject<void>();

  constructor() {
    effect(() => {
      this.noteContentElements();
      untracked(() => {
        this._checkNoteOverflow();
      });
    });
  }

  ngOnInit(): void {
    this._loadCurrentUser();
    this._initNoteMenuItems();
    this._watchForExternalEditorReset();
  }

  /**
   * Checks which notes exceed the height threshold and updates the overflowingNotes set.
   * Only called when notes change, not on every change detection cycle.
   */
  private _checkNoteOverflow(): void {
    if (!this.noteContentElements().length) {
      return;
    }

    const notes = this.getNotes();
    const newOverflowing = new Set<string>();

    this.noteContentElements().forEach((elementRef, index) => {
      const note = notes[index];
      if (!note) {
        return;
      }

      const element = elementRef.nativeElement;
      // scrollHeight gives the full content height even if overflow is hidden
      if (element.scrollHeight > COLLAPSE_HEIGHT_PX) {
        newOverflowing.add(note.guid);
      }
    });

    this.overflowingNotes.set(newOverflowing);
  }

  /**
   * Watches for external editor resets (e.g., from "Save and Continue" via service).
   * When the service resets the store, the component needs to close its editor UI
   * and refresh the notes list to show the newly saved note.
   */
  private _watchForExternalEditorReset(): void {
    this._noteListQuery.editorContent$.pipe(takeUntil(this._destroyed$)).subscribe((content) => {
      // If the store's editor content was reset externally (e.g., by service after save),
      // but our local isEditorOpen is still true, sync them and refresh notes.
      if (!content && this.isEditorOpen() && !this.getEditingNoteGuid()) {
        this.isEditorOpen.set(false);
        this._refreshNotes(); // Refresh to show the newly saved note
        this._cdr.markForCheck();
      }
    });
  }

  /** Initialize menu items for note card ellipsis menu */
  private _initNoteMenuItems(): void {
    this.noteMenuItems = [
      {
        id: MENU_ITEM_ID.EDIT,
        label: 'Edit Note',
        icon: 'pi pi-pencil',
        styleClass: 'note-menu-edit',
        command: () => {
          if (this._activeNote) {
            this.openEditEditor(this._activeNote);
          }
        },
      },
      {
        id: MENU_ITEM_ID.DELETE,
        label: 'Delete Note',
        icon: 'pi pi-trash',
        styleClass: 'note-menu-delete',
        command: () => {
          if (this._activeNote) {
            this.confirmDeleteNote(this._activeNote);
          }
        },
      },
    ];
  }

  /**
   * Handle ellipsis menu click - sets active note, updates delete visibility, and shows menu.
   * Uses `visible` property on delete item instead of rebuilding array to avoid PrimeNG caching issues.
   */
  onNoteMenuClick(event: Event, note: Note): void {
    event.stopPropagation(); // Prevent event bubbling to parent containers
    this._activeNote = note;

    // Update delete item visibility based on whether user can delete this note
    const deleteItem = this.noteMenuItems.find((item) => item.id === MENU_ITEM_ID.DELETE);
    if (deleteItem) {
      deleteItem.visible = this.canDeleteNote(note);
    }

    this.noteMenu().toggle(event);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const fileDataId = this.fileDataId();
    if (changes['fileDataId'] && fileDataId) {
      // If switching to a different loan while editor is open, close the editor UI.
      // This prevents content intended for one loan being displayed/saved for another.
      // Note: Store content is preserved for unsaved changes detection.
      if (!changes['fileDataId'].firstChange && this.isEditorOpen()) {
        this.isEditorOpen.set(false);
      }

      // Coerce to number - route params come in as strings from some parent components (e.g., URLA2)
      const numericFileDataId = typeof fileDataId === 'string' ? +fileDataId : fileDataId;

      // Update store with new entity ID
      this._noteListStore.update({ entityId: numericFileDataId });
      this._loadNotes();
    } else if (changes['refreshTrigger'] && !changes['refreshTrigger'].firstChange && fileDataId) {
      // Same loan clicked again — re-fetch notes without changing the entity ID
      this._loadNotes();
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    // Note: Do NOT reset store here - parent needs to check dirty state
  }

  // ============================================================
  // Editor Actions
  // ============================================================

  /** Opens the editor for creating a new note */
  openCreateEditor(): void {
    this._noteListStore.openCreateEditor();
    this._noteListQuery.dirtyChecker2.dirtyChecker.setHead();
    this.isEditorOpen.set(true);
    // No scroll needed - editor appears in same position as "Create New Note" button
  }

  /** Opens the editor for editing an existing note */
  openEditEditor(note: Note): void {
    // Convert plain text newlines to HTML for legacy notes so editor displays them correctly
    const editorContent = note.isLegacy ? this._convertNewlinesToHtml(note.content) : note.content;
    const tagGuids = note.tags?.map((t) => t.tagGUID) ?? [];
    this._noteListStore.openEditEditor(note.guid, editorContent, note.isLegacy, tagGuids);
    this._noteListQuery.dirtyChecker2.dirtyChecker.setHead();
    this.editingTagGuids.set(tagGuids);
    this.isEditorOpen.set(true);
    this._scrollToTop();
  }

  /** Scrolls the panel to the top so the editor is visible */
  private _scrollToTop(): void {
    // Use setTimeout to ensure the editor is rendered before scrolling
    setTimeout(() => {
      this._elementRef.nativeElement.scrollIntoView({ block: 'start' });
    }, 0);
  }

  /**
   * Closes the editor immediately without confirmation.
   * Per FR-1.5: Cancel button clears content without confirmation modal.
   */
  closeEditor(): void {
    this._resetEditor();
  }

  /**
   * Updates the editor content in the store.
   * Called from template on (ngModelChange).
   */
  onEditorContentChange(content: string): void {
    this._noteListStore.updateEditorContent(content);
  }

  /** Gets current editor content from store (for template bindings) */
  getEditorContent(): string {
    return this._noteListQuery.getValue().editorContent;
  }

  /** Gets current editing note GUID from store (for template bindings) */
  getEditingNoteGuid(): string | null {
    return this._noteListQuery.getValue().editingNoteGuid;
  }

  /** Gets notes from store (for template bindings) */
  getNotes(): Note[] {
    return this._noteListQuery.getValue().notes;
  }

  /** Resets the editor state without confirmation (does NOT reset dirty checker) */
  private _resetEditor(): void {
    this.isEditorOpen.set(false);
    this.editingTagGuids.set([]);
    this._noteListStore.resetEditor();
    // Note: Dirty checker is reset in _refreshNotes() after data is loaded
  }

  /**
   * Saves the note and handles UI side effects (toasts, loading state, editor reset).
   * Called by the Save button in the template.
   *
   * Writes current tag state to the store before saving, then delegates to the service.
   * The service reads content + tags from the store, handling create/update/legacy paths.
   */
  saveNote(): void {
    const content = this.getEditorContent().trim();
    if (!content) {
      return;
    }

    this.isSaving.set(true);

    this._notesPanelService
      .saveCurrentNote()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          this._resetEditor();
          this.isSaving.set(false);
          this._showSuccessToast('Note saved successfully.');
          this._refreshNotes();
        },
        error: () => {
          this.isSaving.set(false);
          this._showErrorToast('Failed to save note. Please try again.');
          this._cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // Note Card Actions
  // ============================================================

  /**
   * Checks if the current user is the author of a note.
   */
  private _isNoteAuthor(note: Note): boolean {
    const userGuid = this.currentUserGuid();
    return !!userGuid && note.insertedBy === userGuid;
  }

  /**
   * Checks if the current user can edit a note.
   * - Legacy notes: All users can edit (per BRD FR-2.2)
   * - V2 notes: Only the note author can edit
   */
  canEditNote(note: Note): boolean {
    return note.isLegacy || this._isNoteAuthor(note);
  }

  /**
   * Checks if the current user can delete a note.
   * - Legacy notes: No one can delete (per BRD FR-2.2)
   * - V2 notes: Only the note author can delete
   */
  canDeleteNote(note: Note): boolean {
    return !note.isLegacy && this._isNoteAuthor(note);
  }

  /** Toggles the expanded state for a note */
  toggleNoteExpanded(noteGuid: string): void {
    this.expandedNotes.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(noteGuid)) {
        newSet.delete(noteGuid);
      } else {
        newSet.add(noteGuid);
      }
      return newSet;
    });
  }

  /** Checks if a note is expanded */
  isNoteExpanded(noteGuid: string): boolean {
    return this.expandedNotes().has(noteGuid);
  }

  /** Checks if a note exceeds the height threshold and needs collapsing */
  isNoteOverflowing(noteGuid: string): boolean {
    return this.overflowingNotes().has(noteGuid);
  }

  // ============================================================
  // Search Actions
  // ============================================================

  /** Toggles the search bar visibility */
  toggleSearch(): void {
    this.isSearchOpen.update((open) => !open);
    // Clear search when closing
    if (!this.isSearchOpen()) {
      this.clearSearch();
    }
  }

  /** Updates the search term as user types */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  /** Executes the search (called on button click or Enter key) */
  executeSearch(): void {
    const term = this.searchTerm().trim();
    if (term.length < 2) {
      return;
    }
    this.activeSearchQuery.set(term);
  }

  /** Clears the search input and results */
  clearSearch(): void {
    this.searchTerm.set('');
    this.activeSearchQuery.set('');
  }

  /**
   * Applies search query to filter notes by content.
   * Per BRD FR-4.4: Search content only (not username/category/date).
   * Per BRD FR-4.5: Case-insensitive.
   * Per BRD FR-4.4 AC-2: Strip HTML tags, search plain text.
   */
  applySearch(notes: Note[]): Note[] {
    const query = this.activeSearchQuery();
    if (!query) {
      return notes;
    }

    const lowerQuery = query.toLowerCase();
    return notes.filter((note) => {
      const plainText = stripHtml(note.content);
      return plainText.toLowerCase().includes(lowerQuery);
    });
  }

  /**
   * Converts plain text newlines to HTML line breaks.
   * Used for legacy notes which store plain text but need to render in HTML context.
   */
  private _convertNewlinesToHtml(text: string): string {
    return text.replace(/\n/g, '<br>');
  }

  /**
   * Highlights search term matches in content.
   * Per BRD FR-4.6: Bold highlight matching text.
   * Also replaces &nbsp; with regular spaces for proper word wrapping.
   * Converts plain text newlines to <br> for legacy notes.
   * Returns original content (with &nbsp; replaced) if no active search.
   */
  highlightSearchTerm(content: string): string {
    // Replace &nbsp; with regular spaces for proper word wrapping and search matching
    let normalized = content.replace(/&nbsp;/gi, ' ');

    // Convert plain text newlines to HTML breaks (for legacy notes that store plain text)
    // V2 notes use <p> tags from Quill, so this only affects legacy notes with \n characters
    if (normalized.includes('\n')) {
      normalized = this._convertNewlinesToHtml(normalized);
    }

    const query = this.activeSearchQuery();
    if (!query) {
      return normalized;
    }

    // Escape special regex characters in the search term
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create case-insensitive regex to find matches
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    // Wrap matches with highlight span (using <mark> for semantic highlighting)
    return normalized.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  // ============================================================
  // Filter Methods
  // ============================================================

  /** Toggle filter panel visibility */
  toggleFilter(event: Event): void {
    const filterPanel = this.filterPanel();
    if (filterPanel) {
      filterPanel.toggle(event);
    }
  }

  /** Called when filter panel opens - reset pending filters to current active filters */
  onFilterPanelShow(): void {
    this.isFilterOpen.set(true);
    this.pendingFilters.set({ ...this.activeFilters() });
  }

  /** Called when filter panel closes */
  onFilterPanelHide(): void {
    this.isFilterOpen.set(false);
  }

  /** Update a pending filter value */
  updatePendingFilter<K extends keyof NotesFilter>(key: K, value: NotesFilter[K]): void {
    this.pendingFilters.update((filters) => ({ ...filters, [key]: value }));
  }

  /** Apply pending filters and close panel */
  applyFilters(): void {
    this.activeFilters.set({ ...this.pendingFilters() });
    this.filterPanel()?.hide();
  }

  /** Clear all filters in pending state */
  clearPendingFilters(): void {
    this.pendingFilters.set({ ...DEFAULT_NOTES_FILTER });
  }

  /** Cancel filter changes and close panel */
  cancelFilters(): void {
    this.filterPanel()?.hide();
  }

  /** Check if any filters are currently active */
  hasActiveFilters(): boolean {
    const filters = this.activeFilters();
    return (
      filters.myNotes ||
      filters.unread ||
      filters.sharedNotes ||
      filters.userGuids.length > 0 ||
      filters.dateFrom !== null ||
      filters.dateTo !== null ||
      filters.tagGUIDs.length > 0
    );
  }

  /**
   * Builds the list of active filter tags for display (BRD FR-5.7).
   * Each tag is removable individually (BRD FR-5.9).
   */
  getActiveFilterTags(availableUsers: NoteUserOption[]): FilterTag[] {
    const filters = this.activeFilters();
    const tags: FilterTag[] = [];

    if (filters.myNotes) {
      tags.push({ label: 'My Notes', type: 'myNotes' });
    }

    if (filters.sharedNotes) {
      tags.push({ label: 'Copied from Scratchpad', type: 'sharedNotes' });
    }

    if (filters.unread) {
      tags.push({ label: 'Unread', type: 'unread' });
    }

    filters.userGuids.forEach((guid) => {
      const user = availableUsers.find((u) => u.guid === guid);
      if (user) {
        tags.push({ label: user.name, type: 'user', value: guid });
      }
    });

    const categoryTags: FilterTag[] = [];
    filters.tagGUIDs.forEach((guid) => {
      const category = this.availableCategories().find((c) => c.guid === guid);
      if (category) {
        categoryTags.push({
          label: category.name,
          type: 'category',
          value: guid,
          backgroundColor: category.backgroundColor,
          textColor: category.textColor,
          hoverBackgroundColor: category.hoverBackgroundColor,
          hoverTextColor: category.hoverTextColor,
        });
      }
    });
    tags.push(...sortByTagOrder(categoryTags, LOAN_NOTE_TAG_ORDER, 'label'));

    // Date range as single tag (BRD FR-5.7 AC-2)
    if (filters.dateFrom || filters.dateTo) {
      const from = filters.dateFrom ? this._formatDateForTag(filters.dateFrom) : '';
      const to = filters.dateTo ? this._formatDateForTag(filters.dateTo) : '';
      if (from && to) {
        tags.push({ label: `${from} - ${to}`, type: 'dateRange' });
      } else if (from) {
        tags.push({ label: `From ${from}`, type: 'dateRange' });
      } else {
        tags.push({ label: `To ${to}`, type: 'dateRange' });
      }
    }

    return tags;
  }

  /**
   * Removes a single filter tag and updates active filters (BRD FR-5.9).
   * Remaining filters stay active (AC-2). Removing last filter resets button (AC-3).
   */
  removeFilterTag(tag: FilterTag): void {
    this.activeFilters.update((filters) => {
      const updated = { ...filters };
      switch (tag.type) {
        case 'myNotes':
          updated.myNotes = false;
          break;
        case 'sharedNotes':
          updated.sharedNotes = false;
          break;
        case 'unread':
          updated.unread = false;
          break;
        case 'user':
          updated.userGuids = filters.userGuids.filter((g) => g !== tag.value);
          break;
        case 'dateRange':
          updated.dateFrom = null;
          updated.dateTo = null;
          break;
        case 'category':
          updated.tagGUIDs = filters.tagGUIDs.filter((g) => g !== tag.value);
          break;
      }
      return updated;
    });
  }

  /** Formats a Date for filter tag display (M/D/YY) */
  private _formatDateForTag(date: Date): string {
    const d = new Date(date);
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  }

  /**
   * Get unique list of users who have created notes (for Users filter dropdown).
   * Derived from current notes list.
   */
  getAvailableUsers(notes: Note[]): NoteUserOption[] {
    const userMap = new Map<string, string>();
    notes.forEach((note) => {
      if (note.insertedBy && note.insertedByName && !userMap.has(note.insertedBy)) {
        userMap.set(note.insertedBy, note.insertedByName);
      }
    });
    return Array.from(userMap.entries())
      .map(([guid, name]) => ({ guid, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Apply filters to notes list.
   * Per BRD FR-5: Filters are applied client-side.
   */
  applyFiltersToNotes(notes: Note[]): Note[] {
    const filters = this.activeFilters();

    // If no filters active, return all notes
    if (!this.hasActiveFilters()) {
      return notes;
    }

    // Pre-compute date boundaries outside the filter loop for performance
    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    if (filters.dateFrom) {
      fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0); // Start of day
    }
    if (filters.dateTo) {
      toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
    }

    return notes.filter((note) => {
      // "Other Type" filters use OR logic - note must match ANY of the checked filters
      const hasOtherTypeFilters = filters.myNotes || filters.sharedNotes || filters.unread;
      if (hasOtherTypeFilters) {
        const isMyNote = note.insertedBy === this.currentUserGuid();
        const isSharedNote = !note.insertedByName; // Legacy notes don't have insertedByName
        const isUnreadNote = note.isUnread;

        const matchesMyNotes = filters.myNotes && isMyNote;
        const matchesSharedNotes = filters.sharedNotes && isSharedNote;
        const matchesUnread = filters.unread && isUnreadNote;

        // Must match at least one of the checked "Other Type" filters
        if (!matchesMyNotes && !matchesSharedNotes && !matchesUnread) {
          return false;
        }
      }

      // Users filter (AND with Other Type filters)
      if (filters.userGuids.length > 0 && !filters.userGuids.includes(note.insertedBy)) {
        return false;
      }

      // Date range filter (BRD line 398: user's local timezone)
      if (fromDate) {
        const noteDate = new Date(note.dateInserted);
        if (noteDate < fromDate) {
          return false;
        }
      }
      if (toDate) {
        const noteDate = new Date(note.dateInserted);
        if (noteDate > toDate) {
          return false;
        }
      }

      // Categories filter — match on tag GUIDs
      if (filters.tagGUIDs.length > 0) {
        const hasMatchingTag = note.tags?.some((tag) => filters.tagGUIDs.includes(tag.tagGUID));
        if (!hasMatchingTag) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Toggles the pinned state of a note.
   * Per BRD FR-3.9: Pinning is global - visible to all users after refresh.
   * Per BRD FR-3.10: User should see pinned note at top immediately.
   */
  togglePin(note: Note): void {
    if (this.isSaving()) {
      return;
    }

    const isPinning = !note.isPinned;
    this.isSaving.set(true);

    this._notesPanelService
      .setNotePinState(note.guid, isPinning)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this._showSuccessToast(isPinning ? 'Note pinned.' : 'Note unpinned.');
          this._refreshNotes();
          // Scroll to top when pinning so user sees the note in its new position
          if (isPinning) {
            this._scrollToTop();
          }
        },
        error: () => {
          this.isSaving.set(false);
          this._showErrorToast(isPinning ? 'Failed to pin note.' : 'Failed to unpin note.');
          this._cdr.markForCheck();
        },
      });
  }

  /**
   * Checks if a note was actually edited (not just auto-updated on creation).
   * Returns true only if dateUpdated is more than 1 second after dateInserted.
   */
  isNoteEdited(note: Note): boolean {
    if (!note.dateUpdated || !note.dateInserted) {
      return false;
    }
    const insertedTime = new Date(note.dateInserted).getTime();
    const updatedTime = new Date(note.dateUpdated).getTime();
    return updatedTime - insertedTime > EDITED_THRESHOLD_MS;
  }

  /** Confirms and deletes a note */
  confirmDeleteNote(note: Note): void {
    this._confirmationService.confirm({
      key: this.confirmDialogKey,
      message: 'Are you sure you want to delete this note?',
      header: 'Delete Confirmation',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectButtonStyleClass: 'btn-pill-secondary',
      acceptButtonStyleClass: 'btn-pill-primary',
      accept: () => {
        this._deleteNote(note.guid);
      },
    });
  }

  /** Retries loading notes after an error */
  retry(): void {
    this._loadNotes();
  }

  // ============================================================
  // Private Methods
  // ============================================================

  /**
   * Sorts notes with pinned first, then by DateInserted descending.
   * Per BRD FR-3.13: Multiple pinned notes sorted by createdAt (not pinnedAt).
   * Per BRD FR-3.14: All pinned notes appear above all unpinned notes.
   */
  private _sortNotes(notes: Note[]): Note[] {
    return [...notes].sort((a, b) => {
      // Pinned notes first
      if (a.isPinned && !b.isPinned) {
        return -1;
      }
      if (!a.isPinned && b.isPinned) {
        return 1;
      }
      // Within same pin status, sort by dateInserted descending (newest first)
      const dateA = new Date(a.dateInserted).getTime();
      const dateB = new Date(b.dateInserted).getTime();
      return dateB - dateA;
    });
  }

  /** Shows a success toast notification */
  private _showSuccessToast(detail: string): void {
    this._messageService.add({ severity: 'success', summary: '', detail });
  }

  /** Shows an error toast notification */
  private _showErrorToast(detail: string): void {
    this._messageService.add({ severity: 'error', summary: '', detail });
  }

  /** Loads the current user's GUID */
  private _loadCurrentUser(): void {
    this._currentUserService
      .getCurrentUserInfo()
      .pipe(take(1))
      .subscribe({
        next: (userInfo) => {
          this.currentUserGuid.set(userInfo?.guid ?? null);
        },
        error: () => {
          // Non-critical - user can still view notes, just can't edit/delete
          this.currentUserGuid.set(null);
        },
      });
  }

  /** Loads notes for the current fileDataId (shows loading spinner) */
  private _loadNotes(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this._notesPanelService
      .getNotes(this.fileDataId())
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: (notes) => {
          this._noteListStore.update({ notes: this._sortNotes(notes) });
          this.isLoading.set(false);
          this._cdr.markForCheck();
          // Mark notes as read when panel opens (fire-and-forget)
          this._markNotesAsRead();
        },
        error: () => {
          this.errorMessage.set('Failed to load notes. Please try again.');
          this.isLoading.set(false);
          this._cdr.markForCheck();
        },
      });
  }

  /** Silently refreshes notes without showing loading spinner (for post-CRUD sync) */
  private _refreshNotes(): void {
    this._notesPanelService
      .getNotes(this.fileDataId())
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: (notes) => {
          this._noteListStore.update({ notes: this._sortNotes(notes) });
          // Reset dirty checker AFTER store is updated to prevent false "unsaved changes"
          this._noteListQuery.dirtyChecker2.dirtyChecker.reset();
          this._markNotesAsRead();
          this._cdr.markForCheck();
          // Re-check overflow after DOM updates (content may have changed height)
          setTimeout(() => {
            this._checkNoteOverflow();
          }, 0);
        },
        // Silently ignore errors - user already saw success toast for their action
      });
  }

  /**
   * Marks all notes as read for the current loan (fire-and-forget).
   * Called when the panel opens to clear unread indicators.
   */
  private _markNotesAsRead(): void {
    this._notesPanelService
      .markAsRead(this.fileDataId())
      .pipe(
        take(1),
        catchError(() => of(undefined)), // Silently ignore errors
      )
      .subscribe();
  }

  /** Called when tags are added or removed in the tags-chips component */
  onTagsChanged(tagGuids: string[]): void {
    this._noteListStore.updateSelectedTagGuids(tagGuids);
  }

  /** Deletes a note */
  private _deleteNote(noteGuid: string): void {
    this.isSaving.set(true);

    this._notesPanelService
      .deleteNote(noteGuid)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this._showSuccessToast('Note deleted successfully.');
          this._refreshNotes();
        },
        error: () => {
          this.isSaving.set(false);
          this._showErrorToast('Failed to delete note. Please try again.');
          this._cdr.markForCheck();
        },
      });
  }
}
